import { Navigate } from 'react-router-dom';
import { useProvince } from '../hooks/useProvince';
import { useAppShellPlatform } from '../hooks/useAppShellPlatform';
import { useKingdom } from '../hooks/useKingdom';
import { useKingdomPolitics } from '../hooks/useKingdomPolitics';
import { useDragonStatus } from '../hooks/useDragonStatus';
import { KINGDOM_ITEMS } from '../constants/kingdomNav';
import { HubCard, type HubCardStat } from '../components/shell/HubCard';
import { formatNumber } from '../utils/format';
import { estimateNetworth, MAX_SHIPMENT_MULTIPLIER } from '../constants/aid';
import type { Kingdom as KingdomEntity } from '../types/kingdom';
import type { PoliticsStandingsResponse } from '../types/politics';
import type { DragonStatus } from '../types/dragon';
import type { Province } from '../types/province';
import type { SiblingItem } from '../types/nav';

/**
 * SF-150 Component 8: `computeStat` reads only `ctx.dragon` (from `useDragonStatus`) for the
 * Dragons card — `ctx.kingdom.dragon` (a second, thinner dragon signal from `useKingdom`) is
 * never referenced here, to avoid two data sources disagreeing on-screen.
 *
 * Exported (unlike RealmHub/WarHub's page-local `computeStat`) so `KingdomHub.test.tsx` can drive
 * the politics/dragon state matrices directly with `it.each(...)` instead of mounting the full
 * component for every case (Phase 3 testability decision) — mirrors the established
 * `react-refresh/only-export-components` suppression pattern already used for a legitimate
 * non-component export (see contexts/ProvinceContext.tsx, contexts/AuthContext.tsx).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function computeStat(item: SiblingItem, ctx: {
  kingdom: KingdomEntity | null;
  kingdomError: string | null;
  politics: PoliticsStandingsResponse | null;
  politicsError: string | null;
  dragon: DragonStatus | null;
  dragonError: string | null;
  province: Province | null;
}): HubCardStat {
  switch (item.key) {
    case 'roster': {
      if (!ctx.kingdom) return { primary: ctx.kingdomError ? 'Kingdom status unavailable' : 'Loading…' };
      const primary = `${ctx.kingdom.stats.totalProvinces} provinces`;
      const atWar = ctx.kingdom.relationships.atWar;
      if (atWar > 0) {
        return { primary, secondary: `${atWar} war${atWar === 1 ? '' : 's'}`, secondaryTone: 'warning', attention: 'warning' };
      }
      return ctx.kingdom.networthRank > 0
        ? { primary, secondary: `Rank #${ctx.kingdom.networthRank} networth` }
        : { primary };
    }
    case 'politics': {
      if (!ctx.politics) return { primary: ctx.politicsError ? 'Politics status unavailable' : 'Loading…' };
      const vacant = !ctx.politics.monarchProvinceId;
      const primary = vacant ? 'Throne vacant' : `Monarch: ${ctx.politics.monarchProvinceName}`;
      const secondary = ctx.politics.myVotedForProvinceId ? 'You have voted' : "You haven't voted";
      return {
        primary,
        secondary,
        secondaryTone: ctx.politics.myVotedForProvinceId ? 'default' : 'warning',
        attention: vacant ? 'warning' : undefined,
      };
    }
    case 'dragons': {
      if (ctx.dragon === null) return { primary: ctx.dragonError ? 'Dragon status unavailable' : 'Loading…' };
      if (!ctx.dragon.hasProject || !ctx.dragon.state) return { primary: 'No dragon project' };
      // role === null is defensive only — the backend contract always sets it when hasProject is
      // true. `=== 'TARGET'` naturally evaluates false for null, so an unset role falls through to
      // the non-target branch: the safer failure direction, since it never overstates threat or
      // invites an action (e.g. "Ready to send") the reader may not be able to take.
      const isTarget = ctx.dragon.role === 'TARGET';
      switch (ctx.dragon.state) {
        case 'FUNDING':
        case 'FUNDED_READY': {
          const funded = ctx.dragon.amountFunded ?? 0;
          const total = ctx.dragon.totalCost ?? 0;
          if (isTarget) {
            return {
              primary: 'Dragon threat detected',
              secondary: total > 0 ? `${Math.round((funded / total) * 100)}% funded` : undefined,
              secondaryTone: 'warning',
              attention: 'warning',
            };
          }
          if (ctx.dragon.state === 'FUNDED_READY') {
            return { primary: 'Ready to send', attention: 'warning' };
          }
          return {
            primary: `${formatNumber(funded)} / ${formatNumber(total)}gc funded`,
            ratio: { value: funded, max: total },
          };
        }
        case 'ACTIVE': {
          const hp = formatNumber(ctx.dragon.hp ?? 0);
          return isTarget
            ? { primary: `${hp} HP active against you`, attention: 'critical' }
            : { primary: `${hp} HP active`, secondary: 'Attacking a rival kingdom' };
        }
        case 'SLAIN':
          return isTarget
            ? { primary: 'Dragon slain', secondary: 'Your kingdom defeated it' }
            : { primary: 'Dragon slain', secondary: 'Your dragon was defeated' };
        case 'EXPIRED':
        case 'CANCELLED':
          return { primary: 'No active dragon project', secondary: 'Previous project ended' };
        default:
          return { primary: 'No dragon project' };
      }
    }
    case 'aid': {
      if (!ctx.province) return { primary: 'Loading…' };
      const nw = estimateNetworth(
        ctx.province.resources.gold,
        ctx.province.population.peasants,
        ctx.province.population.soldiers,
        ctx.province.population.defenseSpecialists,
        ctx.province.population.offenseSpecialists,
        ctx.province.population.elites,
        ctx.province.population.thieves,
        ctx.province.population.wizards,
        ctx.province.territory.totals.barren,
        ctx.province.territory.totals.buildings,
        ctx.province.territory.totals.construction
      );
      const maxShipment = Math.round(nw * MAX_SHIPMENT_MULTIPLIER);
      return { primary: `~${formatNumber(maxShipment)}gc max shipment` };
    }
    default:
      return { primary: '' };
  }
}

/**
 * SF-150 Component 8: `/kingdom` hub screen, structurally identical to `RealmHub.tsx`/
 * `WarHub.tsx` — a navigation surface, not a data-display page. Diverges from Realm/War in one
 * structural way: three of the four cards depend on three independently-resolving hooks
 * (`useKingdom`/`useKingdomPolitics`/`useDragonStatus`) rather than the one shared `province`
 * every Realm/War card reads. Each `computeStat` branch checks only its own hook's null/error
 * state — a card must never wait on a sibling card's data source before rendering its own value.
 */
export function KingdomHub() {
  const platform = useAppShellPlatform();
  const { province } = useProvince();
  const kingdomId = platform === 'desktop' ? null : (province?.metadata.location.kingdomId ?? null);
  const { kingdom, error: kingdomError } = useKingdom(kingdomId);
  const { data: politics, error: politicsError } = useKingdomPolitics(kingdomId);
  const { data: dragon, error: dragonError } = useDragonStatus(kingdomId);

  if (platform === 'desktop') {
    return <Navigate to={KINGDOM_ITEMS[0].route} replace />;
  }

  return (
    <div>
      <h1 className="text-lg font-bold text-game-50 mb-4">Kingdom</h1>
      <div className="flex flex-col gap-3">
        {KINGDOM_ITEMS.map((item) => (
          <HubCard
            key={item.key}
            item={item}
            stat={computeStat(item, { kingdom, kingdomError, politics, politicsError, dragon, dragonError, province })}
          />
        ))}
      </div>
    </div>
  );
}
